/**
 * Minimal fetch wrapper with centralized 401 handling.
 *
 * Path strategy:
 * - `apiFetch('/v1/chat', ...)` — full path including a `/v1`, `/auth`, etc.
 *   prefix. Useful for the JarvisGPT backend whose REST routes live under
 *   `/v1/*` (chat, history, threads).
 * - `apiFetch('/health', ...)` — works for any path; we don't auto-prefix.
 *
 * Cookie credentials are always included so the cookie-based session works.
 */

let authErrorHandler: (() => void) | null = null
let authErrorFired = false

export function onAuthError(handler: () => void) {
  authErrorHandler = handler
}

export function notifyAuthError() {
  if (authErrorHandler && !authErrorFired) {
    authErrorFired = true
    authErrorHandler()
  }
}

export function resetAuthErrorState() {
  authErrorFired = false
}

export async function apiFetch(
  path: string,
  options: RequestInit = {},
): Promise<Response> {
  const res = await fetch(path, { ...options, credentials: 'include' })
  if (res.status === 401) notifyAuthError()
  return res
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await apiFetch(path)
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }))
    throw new ApiError(res.status, err.detail || res.statusText)
  }
  return res.json()
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await apiFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }))
    throw new ApiError(res.status, err.detail || res.statusText)
  }
  return res.json()
}

export async function apiDelete(path: string): Promise<void> {
  const res = await apiFetch(path, { method: 'DELETE' })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }))
    throw new ApiError(res.status, err.detail || res.statusText)
  }
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  const res = await apiFetch(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }))
    throw new ApiError(res.status, err.detail || res.statusText)
  }
  return res.json()
}

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}
